/**
 * Suggested Identified Needs — accept or dismiss (TRUS-576).
 *
 * A suggestion is EVIDENCE, not a need. It becomes an Identified Need only when
 * an LCM says so, and that conversion is the tracked action the feature exists
 * for: an auditor must be able to answer "where did this need come from, who
 * agreed to it, and on what evidence" from the record alone.
 *
 * Shown on BOTH documents (product decision 2026-10-05) — the assessment, where
 * the evidence was gathered, and the care plan, where the need will live. Same
 * rows, read from two places.
 *
 * THE WORDING IS NOT DECORATION. A claims flag means a claim was filed in the
 * last 12 months, not that the member needs help with it today. The panel says
 * what the EVIDENCE is and never asserts the need, because the LCM is the one
 * making that call and a confident-sounding suggestion pre-empts it.
 */

import React from 'react';
import { AlertCircle, Check, X } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { formsAPI, NeedSuggestion } from '@/services/api/forms';

interface NeedSuggestionsProps {
  memberId: number;
  /** The care plan a need is written into. Without one, accepting is not possible. */
  carePlanInstanceId?: number | null;
  /** Shown above the list so an LCM knows which document they are on. */
  context?: 'assessment' | 'care_plan';
  onAccepted?: () => void;
}

export const NeedSuggestions: React.FC<NeedSuggestionsProps> = ({
  memberId,
  carePlanInstanceId,
  context = 'care_plan',
  onAccepted,
}) => {
  const { t } = useAppTranslation();
  const [items, setItems] = React.useState<NeedSuggestion[] | null>(null);
  const [busy, setBusy] = React.useState<number | null>(null);
  const [dismissing, setDismissing] = React.useState<number | null>(null);
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      const data = await formsAPI.listNeedSuggestions(memberId);
      setItems(Array.isArray(data) ? data : []);
    } catch {
      // A suggestions failure must not take the form down with it.
      setItems([]);
    }
  }, [memberId]);

  React.useEffect(() => {
    if (memberId) load();
  }, [memberId, load]);

  const accept = async (id: number) => {
    if (!carePlanInstanceId) return;
    setBusy(id);
    setError(null);
    try {
      await formsAPI.acceptNeedSuggestion(id, carePlanInstanceId);
      setItems((prev) => (prev || []).filter((s) => s.id !== id));
      onAccepted?.();
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('forms.needAcceptFailed', 'Could not add that need.'));
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (id: number) => {
    if (!reason.trim()) return;
    setBusy(id);
    setError(null);
    try {
      await formsAPI.dismissNeedSuggestion(id, reason);
      setItems((prev) => (prev || []).filter((s) => s.id !== id));
      setDismissing(null);
      setReason('');
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('forms.needDismissFailed', 'Could not dismiss that.'));
    } finally {
      setBusy(null);
    }
  };

  // Nothing to show is the common case and should take no room at all.
  if (!items || items.length === 0) return null;

  const canAccept = !!carePlanInstanceId;

  return (
    <div
      className="rounded-lg p-3 mb-4"
      style={{
        background: 'var(--wf-warning-soft, #FFFBEB)',
        border: '1px solid var(--wf-warning, #B45309)',
      }}
    >
      <div className="flex items-start gap-2 mb-2">
        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--wf-warning, #B45309)' }} />
        <div>
          <div className="text-sm font-semibold" style={{ color: 'var(--wf-text, #111827)' }}>
            {t('forms.suggestedNeeds', 'Suggested needs')}
          </div>
          <div className="text-xs" style={{ color: 'var(--wf-muted, #6B7280)' }}>
            {t(
              'forms.suggestedNeedsHelp',
              'Based on plan data and assessment answers. Confirm with the member before adding one.',
            )}
          </div>
        </div>
      </div>

      {!canAccept && (
        <div className="mb-2 text-xs" style={{ color: 'var(--wf-muted, #6B7280)' }}>
          {t('forms.suggestedNeedsNoCarePlan', 'Open the care plan to add any of these as a need.')}
        </div>
      )}

      {error && (
        <div className="mb-2 text-xs" style={{ color: 'var(--wf-error, #DC2626)' }} role="alert">
          {error}
        </div>
      )}

      <ul className="space-y-2">
        {items.map((s) => (
          <li
            key={s.id}
            className="rounded-md p-2 text-sm"
            style={{ background: 'var(--wf-surface, #FFFFFF)', border: '1px solid var(--wf-border, #E5E7EB)' }}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <span className="font-medium" style={{ color: 'var(--wf-text, #111827)' }}>
                  {s.domain}
                </span>
                {/* EVIDENCE, never an assertion of need. */}
                <div className="text-xs" style={{ color: 'var(--wf-muted, #6B7280)' }}>
                  {s.source === 'mif'
                    ? t('forms.evidenceFromPlan', 'SFHP plan data shows: {{fields}}', {
                        fields: (s.rationale || []).map((r) => r.field).filter(Boolean).join(', '),
                      })
                    : t('forms.evidenceFromAssessment', 'Assessment answers: {{fields}}', {
                        fields: (s.rationale || []).map((r) => r.field).filter(Boolean).join(', '),
                      })}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={!canAccept || busy === s.id}
                  onClick={() => accept(s.id)}
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium disabled:opacity-40"
                  style={{ background: 'var(--wf-success-soft, #ECFDF5)', color: 'var(--wf-success, #16A34A)' }}
                >
                  <Check className="h-3 w-3" /> {t('forms.addAsNeed', 'Add as need')}
                </button>
                <button
                  type="button"
                  disabled={busy === s.id}
                  onClick={() => setDismissing(dismissing === s.id ? null : s.id)}
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs disabled:opacity-40"
                  style={{ color: 'var(--wf-muted, #6B7280)' }}
                >
                  <X className="h-3 w-3" /> {t('forms.dismiss', 'Dismiss')}
                </button>
              </div>
            </div>

            {dismissing === s.id && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  id={`dismiss-reason-${s.id}`}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('forms.dismissReasonPlaceholder', 'Why is this not a need?')}
                  className="flex-1 min-w-[12rem] rounded border px-2 py-1 text-xs"
                  style={{ borderColor: 'var(--wf-border, #E5E7EB)' }}
                />
                {/* The reason is required because a dismissal is PERMANENT: no
                    later MIF file re-raises it. Declining a flagged need without
                    saying why leaves an unexplained hole in the record. */}
                <button
                  type="button"
                  disabled={!reason.trim() || busy === s.id}
                  onClick={() => dismiss(s.id)}
                  className="rounded px-2 py-1 text-xs font-medium disabled:opacity-40"
                  style={{ background: 'var(--wf-error-soft, #FEF2F2)', color: 'var(--wf-error, #DC2626)' }}
                >
                  {t('forms.confirmDismiss', 'Dismiss permanently')}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default NeedSuggestions;
